import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth, db } from '../firebase';
import '../styles/Billing.css';

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

const FREE_ALLOWANCE = 10;
const PRICE_PER_SEAT = 5;

const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconCard = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconAlert = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const STATUS_LABELS = {
  pending_first_payment: 'Confirming payment',
  active: 'Active',
  past_due: 'Payment failed',
  failed: 'Setup failed',
  canceled: 'Cancelled',
};

const INVOICE_STATUS_LABELS = {
  paid: 'Paid',
  open: 'Awaiting payment',
  void: 'Voided',
  uncollectible: 'Failed',
  draft: 'Draft',
};

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDate(timestamp) {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ddCost(units, billingInterval) {
  return billingInterval === 'annual'
    ? units * PRICE_PER_SEAT * 12 * 0.9
    : units * PRICE_PER_SEAT;
}

function BillingSection({ companyData, teams = [] }) {
  const [billing, setBilling] = useState(null);
  const [directDebits, setDirectDebits] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [addUnits, setAddUnits] = useState('');
  const [addInterval, setAddInterval] = useState('monthly');
  const [addBusy, setAddBusy] = useState(false);
  const [waiverChecked, setWaiverChecked] = useState(false);

  const [bankSetupSecret, setBankSetupSecret] = useState(null);
  const [bankSetupBusy, setBankSetupBusy] = useState(false);
  const [removeCardBusy, setRemoveCardBusy] = useState(false);
  const [redirectNotice, setRedirectNotice] = useState(null);
  const [autoPromptedCard, setAutoPromptedCard] = useState(false);

  const [reduceTarget, setReduceTarget] = useState(null);
  const [busyDdId, setBusyDdId] = useState(null);

  useEffect(() => {
    if (companyData?.id) loadAll(companyData.id);
  }, [companyData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('setup_intent_client_secret');
    if (!clientSecret) return;

    (async () => {
      const stripe = await getStripe();
      const { setupIntent, error: retrieveError } = await stripe.retrieveSetupIntent(clientSecret);

      window.history.replaceState({}, '', window.location.pathname + window.location.hash);

      if (retrieveError) {
        setRedirectNotice({ type: 'error', message: 'We could not confirm your card. Please try adding it again.' });
        return;
      }

      switch (setupIntent.status) {
        case 'succeeded':
          setRedirectNotice({
            type: 'processing',
            message: 'Your card was confirmed with your bank. Saving the details now...',
          });
          pollForCardConfirmation();
          break;
        case 'processing':
          setRedirectNotice({
            type: 'processing',
            message: 'Your bank is still confirming this card. Check back in a moment and refresh.',
          });
          break;
        default:
          setRedirectNotice({ type: 'error', message: 'Card setup did not complete. Please try adding it again.' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async (companyId) => {
    setLoading(true);
    setError('');
    try {
      const billingSnap = await getDoc(doc(db, 'billingLimits', companyId));
      if (!billingSnap.exists()) {
        setError('No billing record found for your company. Please contact support.');
        return;
      }
      const billingData = billingSnap.data();
      setBilling(billingData);

      if (!billingData.stripeDefaultPaymentMethodId && !autoPromptedCard) {
        setAutoPromptedCard(true);
        handleUpdatePaymentMethod();
      }

      const ddQuery = query(
        collection(db, 'billingLimits', companyId, 'directDebits'),
        orderBy('createdAt', 'desc')
      );
      const ddSnap = await getDocs(ddQuery);
      let ddList = ddSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDirectDebits(ddList);

      const staleDds = ddList.filter((dd) => dd.status === 'pending_first_payment' || dd.status === 'past_due');
      if (staleDds.length > 0) {
        try {
          const functions = getFunctions();
          const syncDirectDebitStatus = httpsCallable(functions, 'syncDirectDebitStatus');
          const results = await Promise.all(
            staleDds.map((dd) => syncDirectDebitStatus({ directDebitId: dd.id }).catch((e) => {
              console.error('Error syncing direct debit status:', e);
              return null;
            }))
          );
          const anyCorrected = results.some((r) => r && r.data && r.data.corrected);
          if (anyCorrected) {
            const refreshedSnap = await getDocs(ddQuery);
            ddList = refreshedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setDirectDebits(ddList);
            const refreshedBilling = await getDoc(doc(db, 'billingLimits', companyId));
            if (refreshedBilling.exists()) setBilling(refreshedBilling.data());
          }
        } catch (e) {
          console.error('Error syncing direct debit statuses:', e);
        }
      }

      try {
        const functions = getFunctions();
        const listPaymentHistory = httpsCallable(functions, 'listPaymentHistory');
        const result = await listPaymentHistory();
        setPaymentHistory(result.data.invoices || []);
      } catch (e) {
        console.error('Error loading payment history:', e);
      }
    } catch (e) {
      console.error('Error loading billing info:', e);
      setError('Failed to load billing information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => companyData?.id && loadAll(companyData.id);

  const pollForCardConfirmation = async () => {
    const companyId = companyData?.id;
    if (!companyId) return;

    const maxAttempts = 8;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const billingSnap = await getDoc(doc(db, 'billingLimits', companyId));
        const data = billingSnap.exists() ? billingSnap.data() : null;
        if (data && data.stripeCardLast4) {
          setRedirectNotice({ type: 'success', message: 'Your card has been confirmed and saved.' });
          await refresh();
          setTimeout(() => setRedirectNotice(null), 4000);
          return;
        }
      } catch (e) {
        console.error('Error polling for card confirmation:', e);
      }
    }

    setRedirectNotice({
      type: 'processing',
      message: 'Your card has been saved with your bank. It can take a couple of minutes to appear here — you can keep using Zella normally and refresh this page in a moment.',
    });
    await refresh();
  };

  const handleCardConfirmed = async () => {
    setBankSetupSecret(null);
    setRedirectNotice({ type: 'processing', message: 'Card saved. Confirming with your bank — this usually takes a few seconds but can occasionally take a couple of minutes. Your card is saved and will appear here once confirmed.' });
    await pollForCardConfirmation();
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const units = parseInt(addUnits, 10);
    if (!Number.isInteger(units) || units <= 0) {
      alert('Enter a whole number greater than 0');
      return;
    }

    const cost = ddCost(units, addInterval).toFixed(2);
    const cadence = addInterval === 'annual' ? 'once a year' : 'every 30 days';
    const confirmed = window.confirm(
      'Schedule a payment of £' + cost + ' ' + cadence + ' for ' + units + ' additional employee' + (units !== 1 ? 's' : '') + '?\n\n' +
      'You will be charged as soon as this is confirmed, then automatically again ' + cadence + '.'
    );
    if (!confirmed) return;

    setAddBusy(true);
    try {
      const functions = getFunctions();
      const requestAddDirectDebit = httpsCallable(functions, 'requestAddDirectDebit');
      const result = await requestAddDirectDebit({ unitsToAdd: units, billingInterval: addInterval });
      if (result.data.status === 'needs_setup') {
        setBankSetupSecret(result.data.clientSecret);
      } else {
        alert('Payment scheduled. Confirming your first charge now.');
        setAddUnits('');
        setWaiverChecked(false);
        refresh();
      }
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to set up the payment. Please try again.');
    } finally {
      setAddBusy(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setBankSetupBusy(true);
    try {
      const functions = getFunctions();
      const requestUpdatePaymentMethod = httpsCallable(functions, 'requestUpdatePaymentMethod');
      const result = await requestUpdatePaymentMethod();
      setBankSetupSecret(result.data.clientSecret);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to start payment method update.');
    } finally {
      setBankSetupBusy(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!window.confirm('Remove this card? You will need to add a new one before scheduling any further payments.')) return;
    setRemoveCardBusy(true);
    try {
      const functions = getFunctions();
      const removePaymentMethod = httpsCallable(functions, 'removePaymentMethod');
      await removePaymentMethod();
      setAutoPromptedCard(false);
      refresh();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to remove the card.');
    } finally {
      setRemoveCardBusy(false);
    }
  };

  const handleCancelDd = async (ddId) => {
    if (!window.confirm('Cancel this scheduled payment? You will keep the employees it covers until your current paid period ends, then they will be removed from your limit.')) return;
    setBusyDdId(ddId);
    try {
      const functions = getFunctions();
      const cancelDirectDebit = httpsCallable(functions, 'cancelDirectDebit');
      await cancelDirectDebit({ directDebitId: ddId });
      refresh();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to cancel.');
    } finally {
      setBusyDdId(null);
    }
  };

  const handleReduceDd = async (ddId, currentUnits) => {
    const value = reduceTarget && reduceTarget.id === ddId ? parseInt(reduceTarget.value, 10) : NaN;
    if (!Number.isInteger(value) || value < 0 || value >= currentUnits) {
      alert('Enter a whole number less than ' + currentUnits);
      return;
    }
    setBusyDdId(ddId);
    try {
      const functions = getFunctions();
      const reduceDirectDebitUnits = httpsCallable(functions, 'reduceDirectDebitUnits');
      await reduceDirectDebitUnits({ directDebitId: ddId, newUnits: value });
      setReduceTarget(null);
      refresh();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to update.');
    } finally {
      setBusyDdId(null);
    }
  };

  const handleSyncDd = async (ddId) => {
    setBusyDdId(ddId);
    try {
      const functions = getFunctions();
      const syncDirectDebitStatus = httpsCallable(functions, 'syncDirectDebitStatus');
      const result = await syncDirectDebitStatus({ directDebitId: ddId });
      if (result.data && result.data.corrected) {
        refresh();
      } else {
        alert('Still waiting on this payment to confirm with Stripe. Try again in a moment.');
      }
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to check status.');
    } finally {
      setBusyDdId(null);
    }
  };

  const handleRetryDd = async (ddId) => {
    setBusyDdId(ddId);
    try {
      const functions = getFunctions();
      const retryDirectDebitPayment = httpsCallable(functions, 'retryDirectDebitPayment');
      await retryDirectDebitPayment({ directDebitId: ddId });
      alert('Payment retried. Refresh in a moment to see the result.');
      refresh();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Retry failed. Check your card details.');
    } finally {
      setBusyDdId(null);
    }
  };

  if (loading) {
    return (
      <>
        <div className="dashboard-header"><div><h1>Payments &amp; Billing</h1></div></div>
        <p className="billing-loading-text">Loading billing information...</p>
      </>
    );
  }

  if (error || !billing) {
    return (
      <>
        <div className="dashboard-header"><div><h1>Payments &amp; Billing</h1></div></div>
        <div className="error-message">{error || 'Something went wrong.'}</div>
      </>
    );
  }

  const used = teams.reduce(function (sum, t) { return sum + (t.memberCount || 0); }, 0);
  const limit = billing.limit || 0;
  const billedUnits = Math.max(limit - FREE_ALLOWANCE, 0);
  const usagePercent = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isNearLimit = limit > 0 && used / limit >= 0.9;
  const remaining = Math.max(limit - used, 0);
  const addUnitsNum = parseInt(addUnits, 10) || 0;
  const hasCard = !!billing.stripeCardLast4;

  return (
    <div className="billing-content">
      <div className="dashboard-header">
        <div>
          <h1>Payments &amp; Billing</h1>
          <p>Manage your employee limit, scheduled payments, and card details</p>
        </div>
      </div>

      {redirectNotice && (
        <div className={'billing-redirect-banner ' + redirectNotice.type}>
          {redirectNotice.message}
        </div>
      )}

      {!billing.active && (
        <div className="billing-alert-banner">
          <IconLock />
          <div>
            <strong>Access is currently restricted</strong>
            <p>Your account is marked inactive. Contact support if you believe this is a mistake.</p>
          </div>
        </div>
      )}

      <div className="settings-card">
        <div className="card-header">
          <h3>Payment Card</h3>
        </div>

        {bankSetupSecret ? (
          <CardSetupForm
            clientSecret={bankSetupSecret}
            onDone={handleCardConfirmed}
            onCancel={function () { setBankSetupSecret(null); }}
          />
        ) : hasCard ? (
          <div className="billing-card-display">
            <div className="billing-card-display-info">
              <IconCard />
              <div>
                <span className="billing-card-display-number">
                  {capitalize(billing.stripeCardBrand)} ending in {billing.stripeCardLast4}
                </span>
                <span className="billing-card-display-note">Used for all scheduled payments</span>
              </div>
            </div>
            <div className="billing-card-display-actions">
              <button className="btn-edit" onClick={handleUpdatePaymentMethod} disabled={bankSetupBusy}>
                {bankSetupBusy ? <span className="billing-spinner" /> : 'Update Card'}
              </button>
              <button className="billing-dd-link-btn billing-dd-link-danger" onClick={handleRemoveCard} disabled={removeCardBusy}>
                {removeCardBusy ? <span className="billing-spinner" /> : 'Remove Card'}
              </button>
            </div>
          </div>
        ) : (
          <div className="billing-card-display">
            <p className="billing-help-text">You do not have a payment card on file yet.</p>
            <button className="btn-edit" onClick={handleUpdatePaymentMethod} disabled={bankSetupBusy}>
              {bankSetupBusy ? <span className="billing-spinner" /> : 'Add a Card'}
            </button>
          </div>
        )}
      </div>

      <div className="billing-top-row">
        <div className="billing-stat-card billing-stat-narrow">
          <h3>Employee Limit</h3>
          <p className="billing-stat-value">{limit}</p>
          <p className="billing-stat-meta">{FREE_ALLOWANCE} free, {billedUnits} paid</p>
        </div>
        <div className="billing-stat-card billing-stat-narrow">
          <h3>Approved Employees</h3>
          <p className="billing-stat-value">{used}</p>
          <p className="billing-stat-meta">{remaining} remaining</p>
        </div>
        <div className="billing-stat-card billing-stat-wide">
          <h3>Usage</h3>
          <div className="billing-usage-track">
            <div
              className={'billing-usage-fill ' + (isNearLimit ? 'near-limit' : '')}
              style={{ width: usagePercent + '%' }}
            />
          </div>
          <p className="billing-usage-label">{used} / {limit} employees approved</p>
        </div>
      </div>

      <div className="settings-card">
        <h3>Increase Your Employee Limit</h3>
        <p className="billing-help-text">
          Here is how this works. Your first {FREE_ALLOWANCE} employees are free. Each employee beyond that costs
          £{PRICE_PER_SEAT} per month, or you can save 10% by paying yearly instead. You are charged as soon as
          your card payment is confirmed, then automatically again every 30 days (or once a year, if you chose
          yearly billing).
        </p>
        <p className="billing-help-text">
          Each scheduled payment you set up is separate from the others, so you can lower the amount or cancel
          any one of them on its own without affecting the rest. If a scheduled payment fails, or you cancel
          one, the portion of the limit covered by that payment is removed from your limit whenever the paid
          period is over. If your approved employee count then goes above your new limit, your Reports,
          Analytics, and Overview alerts will be locked until you bring your limit back up or remove employees
          to fit within it. Everything else in Zella keeps working as normal.
        </p>
        <form onSubmit={handleAddSubmit} className="billing-add-form">
          <div className="form-group">
            <label htmlFor="addUnits">How many extra employees do you need?</label>
            <input
              id="addUnits"
              type="number"
              min="1"
              value={addUnits}
              onChange={function (e) { setAddUnits(e.target.value); }}
              placeholder="e.g. 20"
              disabled={addBusy}
            />
          </div>
          <div className="form-group">
            <label>Billing</label>
            <div className="billing-interval-toggle">
              <button
                type="button"
                className={addInterval === 'monthly' ? 'active' : ''}
                onClick={function () { setAddInterval('monthly'); }}
              >
                Every 30 days
              </button>
              <button
                type="button"
                className={addInterval === 'annual' ? 'active' : ''}
                onClick={function () { setAddInterval('annual'); }}
              >
                Once a year (save 10%)
              </button>
            </div>
          </div>
          {addUnitsNum > 0 && (
            <p className="billing-add-cost-preview">
              £{ddCost(addUnitsNum, addInterval).toFixed(2)} charged {addInterval === 'annual' ? 'once a year' : 'every 30 days'}
            </p>
          )}
          <div className="billing-waiver-row">
            <input
              type="checkbox"
              id="billingWaiver"
              checked={waiverChecked}
              onChange={function (e) { setWaiverChecked(e.target.checked); }}
              disabled={addBusy}
            />
            <label htmlFor="billingWaiver">
              I understand that billing starts immediately and I waive my right to a 14-day cancellation period,
              as the service begins straight away.
            </label>
          </div>
          <button type="submit" className="btn-save" disabled={addBusy || !waiverChecked}>
            {addBusy ? <span className="billing-spinner" /> : 'Schedule Payment'}
          </button>
        </form>
      </div>

      <div className="settings-card">
        <div className="card-header">
          <h3>Scheduled Payments</h3>
        </div>

        {directDebits.length === 0 ? (
          <p className="billing-help-text">
            You don't have any scheduled payments yet. Your first {FREE_ALLOWANCE} employees are free.
            Add one above once you need more.
          </p>
        ) : (
          <div className="billing-dd-list">
            {directDebits.map(function (dd) {
              const isCancelling = dd.status === 'active' && dd.cancelAtPeriodEnd;
              const periodEndLabel = formatDate(dd.currentPeriodEnd);
              return (
              <div key={dd.id} className={'billing-dd-row billing-dd-' + dd.status + (isCancelling ? ' billing-dd-cancelling' : '')}>
                <div className="billing-dd-info">
                  <span className="billing-dd-units">+{dd.limitUnits} employees</span>
                  <span className="billing-dd-meta">
                    {dd.billingInterval === 'annual' ? 'Billed yearly' : 'Billed every 30 days'} · £{ddCost(dd.limitUnits, dd.billingInterval).toFixed(2)} {dd.billingInterval === 'annual' ? '/ year' : '/ month'}
                  </span>
                  {isCancelling ? (
                    <span className="billing-dd-status-pill billing-dd-status-cancelling">
                      <IconAlert />
                      {periodEndLabel ? 'Cancelling, ends ' + periodEndLabel : 'Cancelling at period end'}
                    </span>
                  ) : (
                    <span className={'billing-dd-status-pill billing-dd-status-' + dd.status}>
                      {(dd.status === 'past_due' || dd.status === 'failed') && <IconAlert />}
                      {STATUS_LABELS[dd.status] || dd.status}
                    </span>
                  )}
                </div>

                {dd.status === 'pending_first_payment' && (
                  <div className="billing-dd-actions">
                    <button
                      className="billing-dd-link-btn"
                      onClick={function () { handleSyncDd(dd.id); }}
                      disabled={busyDdId === dd.id}
                    >
                      {busyDdId === dd.id ? <span className="billing-spinner" /> : 'Refresh Status'}
                    </button>
                  </div>
                )}

                {dd.status === 'active' && !isCancelling && (
                  <div className="billing-dd-actions">
                    {reduceTarget && reduceTarget.id === dd.id ? (
                      <div className="billing-dd-reduce-form">
                        <input
                          type="number"
                          min="0"
                          max={dd.limitUnits - 1}
                          value={reduceTarget.value}
                          onChange={function (e) { setReduceTarget({ id: dd.id, value: e.target.value }); }}
                          placeholder={'< ' + dd.limitUnits}
                        />
                        <button onClick={function () { handleReduceDd(dd.id, dd.limitUnits); }} disabled={busyDdId === dd.id}>Save</button>
                        <button className="billing-dd-link-btn" onClick={function () { setReduceTarget(null); }}>Cancel</button>
                      </div>
                    ) : (
                      <button className="billing-dd-link-btn" onClick={function () { setReduceTarget({ id: dd.id, value: '' }); }}>
                        Lower amount
                      </button>
                    )}
                    <button
                      className="billing-dd-link-btn billing-dd-link-danger"
                      onClick={function () { handleCancelDd(dd.id); }}
                      disabled={busyDdId === dd.id}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {dd.status === 'past_due' && (
                  <div className="billing-dd-actions">
                    <button
                      className="btn-edit"
                      onClick={function () { handleRetryDd(dd.id); }}
                      disabled={busyDdId === dd.id}
                    >
                      {busyDdId === dd.id ? <span className="billing-spinner" /> : 'Retry Payment'}
                    </button>
                  </div>
                )}

                {dd.status === 'failed' && (
                  <div className="billing-dd-actions">
                    <p className="billing-help-text" style={{ margin: 0, fontSize: '0.82rem' }}>
                      Initial setup failed. Schedule a new payment to try again.
                    </p>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="settings-card">
        <div className="card-header">
          <h3>Payment History</h3>
        </div>

        {paymentHistory.length === 0 ? (
          <p className="billing-help-text">No payments have been made yet.</p>
        ) : (
          <div className="billing-history-list">
            {paymentHistory.map(function (inv) {
              return (
                <div key={inv.id} className="billing-history-row">
                  <div className="billing-history-info">
                    <span className="billing-history-desc">{inv.description}</span>
                    <span className="billing-history-date">{new Date(inv.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  <span className="billing-history-amount">£{inv.amount.toFixed(2)}</span>
                  <span className={'billing-history-status billing-history-status-' + inv.status}>
                    {INVOICE_STATUS_LABELS[inv.status] || inv.status}
                  </span>
                  {inv.hostedInvoiceUrl && (
                    <a className="billing-history-link" href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                      View
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CardSetupForm({ clientSecret, onDone, onCancel }) {
  return (
    <Elements stripe={getStripe()} options={{ clientSecret: clientSecret }}>
      <CardSetupFormInner onDone={onDone} onCancel={onCancel} />
    </Elements>
  );
}

function CardSetupFormInner({ onDone, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setFormError('');
    setBusy(true);
    try {
      const result = await stripe.confirmSetup({
        elements: elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });
      if (result.error) {
        setFormError(result.error.message || 'Could not save card. Check your details and try again.');
        return;
      }
      if (result.setupIntent && result.setupIntent.status === 'succeeded') {
        onDone();
      } else {
        setFormError('Card setup did not complete. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setFormError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="billing-card-form-highlight">
      {formError && <div className="error-message">{formError}</div>}
      <form onSubmit={handleSubmit} className="edit-form">
        <div className="billing-card-element-wrapper">
          <p className="billing-help-text">
            Billing address — this appears on your invoices, so please use your registered company address.
          </p>
          <AddressElement options={{ mode: 'billing' }} />
          <PaymentElement
            options={{
              wallets: { link: 'never' },
              fields: { billingDetails: { address: 'never' } },
            }}
          />
        </div>
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-save" disabled={busy || !stripe}>
            {busy ? <span className="billing-spinner" /> : 'Confirm Card'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BillingSection;